import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import {
  DietDetails,
  Folder,
  FolderCategories
} from '../../../api/services/dietbase/types.ts'
import { OrderDetailsType } from '../../../api/services/orders/types.ts'
import useDietBaseFolders from '../../../hooks/useDietBaseFolders.ts'
import {
  filterFolders,
  removeDuplicatesForCalories
} from '../../../utils/folders.ts'
import { findAnswerTextByExtraValue } from '../../../utils/orders/orderDetailsHelpers.ts'
import InfoForDietitian from '../../orders/infoForDietitian/InfoForDietitian.tsx'
import { OrderDetailsAnswers } from '../../orders/orderDetailsAnswers/OrderDetailsAnswers.tsx'
import BottomButtons from './BottomButtons/BottomButtons.tsx'
import DaymenusDropdown from './DaymenusDropdown'
import DisplayFolders from './DisplayFolders/DisplayFolders.tsx'
import FolderNames from './FolderNames/FolderNames.tsx'
import FoldersFilter from './FoldersFilter'
import HeaderSection from './HeaderSection'

const DietSelector = ({
  order,
  setChangeStatus,
  onClose,
  isPopup = true,
  getDietDetails,
  selectedDietID,
  getStatusLoadingDietList,
  getOptionSharingDiet,
  getSelectedFolder
}: {
  order: OrderDetailsType | null
  setChangeStatus: React.Dispatch<React.SetStateAction<boolean>>
  onClose?: () => void
  isPopup?: boolean
  getDietDetails?: (details: DietDetails | null, dietID: number | null) => void
  selectedDietID: number | null
  getStatusLoadingDietList?: (status: boolean) => void
  getOptionSharingDiet?: (option: 'folder' | 'diet' | null) => void
  getSelectedFolder?: (folder: number | null) => void
}) => {
  const { categoryTitles, categoryObjects, fetchFolders, fetchCategories } =
    useDietBaseFolders()

  const foldersFilterRef = useRef<HTMLDivElement | null>(null)

  const [daymenusSelected, setDaymenusSelected] = useState<string | null>(null)
  const [folderNames, setFolderNames] = useState<FolderCategories[]>([])
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(
    null
  )
  const [folderNamesDropdownOptions, setFolderNamesDropdownOptions] = useState<
    string[]
  >([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [filteredFolders, setFilteredFolders] = useState<Folder[]>([])
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)
  const mealsDropdownOptions = [3, 4, 5, 'wszystkie opcje']
  const [indexOfMealsDropdownList, setIndexOfMealsDropdownList] = useState(0)
  const [energyDropdownOptions, setEnergyDropdownOptions] = useState<
    (string | number)[]
  >([])
  const [indexOfEnergyDropdownList, setIndexOfEnergyDropdownList] = useState(0)
  const [selectedMeals, setSelectedMeals] = useState<number | string | null>(
    'wszystkie opcje'
  )
  const [selectedEnergy, setSelectedEnergy] = useState<number | string | null>(
    'wszystkie opcje'
  )
  const [showSurvey, setShowSurvey] = useState<boolean>(false)

  const [optionSharingSelected, setOptionSharingSelected] = React.useState<
    'folder' | 'diet' | null
  >(null)

  let suggestedCaloricIntake = ''

  useLayoutEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    if (optionSharingSelected === 'diet') {
      filteredFolders.map((folder) => {
        folder.selected = false

        return folder
      })
      setSelectedFolder(null)
    }
    getOptionSharingDiet?.(optionSharingSelected)
  }, [optionSharingSelected])

  useEffect(() => {
    if (categoryTitles && categoryObjects) {
      const filteredObjects = categoryObjects.filter((obj) =>
        daymenusSelected === 'global' ? obj.is_global : !obj.is_global
      )
      const filteredTitles = filteredObjects.map((obj) => obj.name)
      setFolderNamesDropdownOptions(filteredTitles)
      setFolderNames(filteredObjects)
    }
  }, [categoryTitles, categoryObjects, daymenusSelected])

  useEffect(() => {
    const isExistsNumberOfMealsInOrder = order
      ? findAnswerTextByExtraValue(order.questions, 'number_of_meals')
      : undefined

    const NumberOfMealsInOrder = isExistsNumberOfMealsInOrder
      ? Number(isExistsNumberOfMealsInOrder)
      : 0

    const numberOfMeals: string | number =
      NumberOfMealsInOrder > 0 ? NumberOfMealsInOrder : 'wszystkie opcje'

    const index = mealsDropdownOptions.indexOf(numberOfMeals)

    setIndexOfMealsDropdownList(index)
    setSelectedMeals(mealsDropdownOptions[index])
  }, [])

  const handleSOptionSharing = useCallback(
    (option: 'folder' | 'diet' | null) => {
      setOptionSharingSelected(option)
    },
    []
  )
  const gettingFolders = async () => {
    const folderName = folderNames.find(
      (folder) => folder.name === selectedFolderName
    )

    if (order && folderName) {
      const { folders, energyRange } = await fetchFolders(
        folderName.id || 0,
        order.patient_profile.id
      )

      if (folders && energyRange) {
        setSelectedEnergy('wszystkie opcje')

        const caloriesWithoutDuplicates =
          removeDuplicatesForCalories(energyRange)

        setEnergyDropdownOptions(caloriesWithoutDuplicates)
        setIndexOfEnergyDropdownList(
          caloriesWithoutDuplicates.indexOf('wszystkie opcje')
        )

        setFolders(folders)
        setFilteredFolders(folders)
      }
    }
  }

  useEffect(() => {
    if (selectedFolderName !== null) {
      gettingFolders()
      handleSelectionFolder(0)
    }
  }, [selectedFolderName])

  const handleSelectionFolder = useCallback(
    (id: number) => {
      const folder = filteredFolders.find((folder) => folder.id === id)

      if (folder) {
        filteredFolders.map((folder) => {
          if (folder.id === id) {
            folder.selected = !folder.selected
          } else {
            folder.selected = false
          }
          return folder
        })
        setSelectedFolder(folder.selected ? id : null)
      } else {
        filteredFolders.map((folder) => {
          folder.selected = false
          return folder
        })
        setSelectedFolder(null)
      }
      setFilteredFolders(filteredFolders)
    },
    [filteredFolders]
  )

  const handleSelectionFolderNames = (folderName: string | null) => {
    const selectedFolderName: FolderCategories = folderNames.find(
      (group) => group.name === folderName
    ) || { id: 0, name: '', is_global: false, folder_count: 0 }
    setSelectedFolderName(selectedFolderName?.name)
  }

    useEffect(() => {
      if (selectedFolderName !== null && foldersFilterRef.current) {
        foldersFilterRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }, [selectedFolderName, filteredFolders.length])

  const handleSelectionMeals = (selected: number | string | null) => {
    handleSelectionFolder(0)
    setSelectedMeals(selected)
  }

  const handleSelectionEnergy = (selected: number | string | null) => {
    handleSelectionFolder(0)
    setSelectedEnergy(selected)
  }

  const handleOnClickClose = () => {
    onClose && onClose()
    handleReset()
  }

  const handleReset = () => {
    setSelectedFolderName(null)
    setSelectedMeals(mealsDropdownOptions[indexOfMealsDropdownList])
    setSelectedEnergy('wszystkie opcje')
    handleSelectionFolder(0)
    getDietDetails?.(null, null)
  }

  const handleSelectionDaymenus = (daymenus: string | null) => {
    setDaymenusSelected(daymenus)
  }

  useEffect(() => {
    const filtered = filterFolders(folders, selectedMeals, selectedEnergy)
    setFilteredFolders(filtered)
  }, [folders, selectedMeals, selectedEnergy])

  if (order && order.suggested_caloric_intake !== null) {
    suggestedCaloricIntake =
      order.suggested_caloric_intake.length > 1
        ? `${Math.round(order?.suggested_caloric_intake[0])} - ${Math.round(order?.suggested_caloric_intake[1])}`
        : `${Math.round(order?.suggested_caloric_intake[0])}`
  }

  return (
    <React.Fragment>
      {isPopup && (
        <>
          <h2 className="px-6 py-3 text-xl font-semibold text-center md:text-left">
            {'Wybierz folder z dietami dla pacjenta'}
          </h2>
          <div className={'border dark:border-gray-700'}></div>
        </>
      )}

      {order && (
        <div className={'px-0 py-3'}>
          <div className="my-1 dark:border-gray-700">
            <HeaderSection
              order={order}
              setShowSurvey={setShowSurvey}
              showSurvey={showSurvey}
            />

            {showSurvey && (
              <OrderDetailsAnswers orderDetails={order} horizontal={true} />
            )}

            <div className={'flex justify-center items-center'}>
              <div className="flex flex-col bg-blue-100 dark:bg-blue-900 mt-2 p-2">
                {order.ppm && (
                  <InfoForDietitian
                    label={'Podstawowa Przemiana Materii (PPM) :'}
                    data={Math.round(order.ppm)}
                    suffix={' kcal'}
                  />
                )}
                {order.cpm && (
                  <InfoForDietitian
                    label={'Całkowita przemiana materii (CPM) :'}
                    data={Math.round(order.cpm)}
                    suffix={' kcal'}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DaymenusDropdown
        daymenusSelected={daymenusSelected}
        setDaymenus={handleSelectionDaymenus}
        resetFilters={handleReset}
      />

      <FolderNames
        options={folderNamesDropdownOptions}
        onSelect={handleSelectionFolderNames}
        selectedFolderName={selectedFolderName}
        onClick={handleReset}
      />

      {selectedFolderName !== '' && selectedFolderName !== null && (
        <div>
          <div
            className={
              'flex flex-col p-5 items-center justify-center gap-x-4 border border-gray-200 dark:border-gray-600'
            }
          >
            <FoldersFilter
              ref={foldersFilterRef}
              mealsDropdownOptions={mealsDropdownOptions}
              handleSelectionMeals={handleSelectionMeals}
              indexOfMealsDropdownList={indexOfMealsDropdownList}
              energyDropdownOptions={energyDropdownOptions}
              handleSelectionEnergy={handleSelectionEnergy}
              indexOfEnergyDropdownList={indexOfEnergyDropdownList}
            />
            <div className="bg-green-100 dark:bg-green-900 mt-2 p-2 flex justify-center items-center">
              {order && order.suggested_caloric_intake && (
                <InfoForDietitian
                  label={'Wyliczona proponowana kaloryczność :'}
                  data={suggestedCaloricIntake}
                  suffix={' (kcal)'}
                  border={false}
                />
              )}
            </div>
          </div>
          <div className="overflow-y-auto ">
            <DisplayFolders
              daymenusSelected={daymenusSelected}
              filteredFolders={filteredFolders}
              handleSelectionFolder={handleSelectionFolder}
              optionSharingSelected={optionSharingSelected}
              setOptionSharing={handleSOptionSharing}
              getDietDetails={getDietDetails}
              getStatusLoadingDietList={getStatusLoadingDietList}
              getSelectedFolder={getSelectedFolder}
            />
          </div>
        </div>
      )}
      <div className={'px-6 pb-6'}>
        <BottomButtons
          order={order}
          orderID={order?.id}
          selectedFolder={selectedFolder}
          selectedDietID={selectedDietID}
          handleOnClickClose={handleOnClickClose}
          setChangeStatus={setChangeStatus}
          optionSharingSelected={optionSharingSelected}
        />
      </div>
    </React.Fragment>
  )
}

export default DietSelector
